import React, {useRef, useState} from "react";

import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import Button from "@/components/buttons/button";
import {BaseDialogProps} from "@/components/dialogs/base-dialog";

interface BuildingFormElements extends HTMLFormControlsCollection {
    buildingName: HTMLInputElement,
}

interface FormElement extends HTMLFormElement {
    readonly elements: BuildingFormElements
}

export interface AddOrEditBuildingDialogProps extends BaseDialogProps {
    buildingId?: string;
}

const AddOrEditBuildingDialog: React.FC<AddOrEditBuildingDialogProps> = ({buildingId, open, onClose}) => {
    const {data, tryAddBuilding, tryEditBuilding} = useProjectOrganisationContext();

    const [error, setError] = useState<string | undefined>(undefined);

    const nameInputRef = useRef(null);

    let cachedName;
    const isEditing = buildingId != null;
    if(isEditing) {
        const building = data?.getBuilding(buildingId);
        if(building) {
            cachedName = building.name;
        }
    } else {
        cachedName = ''
    }

    const closeDialog = function () {
        onClose(true);
        setError(undefined);
    }

    const handleSubmit = function(event: React.FormEvent<FormElement>) {
        event.preventDefault()
        const {elements} = event.currentTarget;
        
        const buildingName = elements.buildingName.value.trim();
        
        if(buildingName == null || buildingName === '')
        {
            setError('Veuillez renseigner un nom');
            return;
        }
        
        if(isEditing) {
            if(buildingId == null)
                return;
            
            try {
                tryEditBuilding(buildingId, buildingName);
                closeDialog();
            } catch (e: unknown) {
                if(e instanceof Error) {
                    setError(e.message);
                }
            }
        } else {
            try {
                tryAddBuilding(buildingName);
                closeDialog();
            }  catch (e: unknown) {
                if(e instanceof Error) {
                    setError(e.message);
                }
            }
        }
    }

    return (
        <ClosableDialog open={open} onClose={closeDialog} title={isEditing ? 'Modifier le bâtiment' : 'Nouveau bâtiment'}>
            <form onSubmit={handleSubmit} className='flex flex-col gap-y-4'>

                {error && <div className='bg-red-400 rounded-sm p-2 text-white'>{error}</div>}

                <input
                    ref={nameInputRef}
                    autoFocus={true}
                    tabIndex={1}
                    id="building-name"
                    name="buildingName"
                    type="text"
                    defaultValue={cachedName}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />

                <Button type='submit' className='ml-auto'>
                    {isEditing ? 'Modifier' : 'Créer'}
                </Button>
            </form>
        </ClosableDialog>
    )
}

export default AddOrEditBuildingDialog;